import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { client, SiteGroupSummaryDto } from "@/client";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useTimeSettings } from "@/state/time-settings";
import { usePermissions } from "@/hooks/use-permissions";
import useBreakpoint from "@/hooks/use-breakpoint";
import { useTooltipProps } from "@/components/custom/tooltip";
import { FloatingMenu } from "@/components/custom/floating-menu";
import { selectColumn } from "@/components/custom/data-table/data-table-select-column";
import { useTableDeleteRefresh } from "@/components/custom/data-table/data-table-context";
import { PiClock } from "react-icons/pi";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { UploadCatalogDialog } from "../../data/catalog/-upload-catalog-dialog";

import { showActionErrorToast } from "@/state/error-toast";

const SiteGroupMenuCell = ({
  siteGroup,
  permissions,
  t,
  siteGroupsClient,
}: {
  siteGroup: SiteGroupSummaryDto;
  permissions: any;
  t: any;
  siteGroupsClient: any;
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const id = siteGroup.id;
  const deleteRefresh = useTableDeleteRefresh();

  const menuRows = [
    permissions.catalog.edit
      ? {
          text: t("uploadCatalog"),
          onClick: () => setUploadDialogOpen(true),
        }
      : null,
    // Gated like the route: DELETE /site-groups/:id is siteGroups.delete, which SiteManager lacks.
    permissions.siteGroups.delete
      ? {
          text: t("delete"),
          onClick: async () => {
            if (confirm(t("confirmDeleteSiteGroup"))) {
              await siteGroupsClient.mutateAsync({ id });
              await deleteRefresh(id);
            }
          },
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <>
      <FloatingMenu menuRows={menuRows} />
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <VisuallyHidden>
            <DialogTitle>{t("uploadCatalog")}</DialogTitle>
            <DialogDescription>
              {t("Upload your product catalog CSV file")}
            </DialogDescription>
          </VisuallyHidden>
          <UploadCatalogDialog
            onClose={() => setUploadDialogOpen(false)}
            initialGroupId={id}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const useColumns = (): ColumnDef<SiteGroupSummaryDto, any>[] => {
  const { t } = useTranslation();
  const timeSettings = useTimeSettings();
  const { permissions } = usePermissions();
  const isMobile = !useBreakpoint("md");
  const tooltipProps = useTooltipProps();
  const navigate = useNavigate();

  const siteGroupsClient = useMutation({
    ...client.siteGroups.delete.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });

  return useMemo(() => {
    const columns: (ColumnDef<SiteGroupSummaryDto, any> | boolean)[] = [
      permissions.sites.edit && (selectColumn as any),
      {
        id: "name",
        header: t("name"),
        accessorFn: (row) => row.name,
        size: isMobile ? 200 : 50,
        meta: {
          // width: isMobile ? "2fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          const truncatedValue =
            value.length > 10 ? value.substring(0, 10) + "..." : value;
          return (
            <div {...tooltipProps} data-tooltip-content={cell.getValue()}>
              {truncatedValue}
            </div>
          );
        },
      },
      !isMobile && {
        id: "sitesCount",
        header: t("sites"),
        accessorFn: (row) => row.sitesCount ?? 0,
        size: isMobile ? 100 : 50,
        meta: {
          // width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      !isMobile && {
        id: "translatedLabelsCount",
        header: t("translatedLabels"),
        accessorFn: (row) => row.translatedLabelsCount ?? 0,
        size: isMobile ? 100 : 50,
        meta: {
          // width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell, row }) => {
          const value = cell.getValue();
          const siteGroupId = row.original.id;

          return (
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => {
                navigate({
                  to: "/dashboard/resources/site-groups/$siteGroupId/labels",
                  params: { siteGroupId: siteGroupId.toString() },
                });
              }}
            >
              {value}
            </span>
          );
        },
      },
      {
        header: t("createdAt"),
        accessorKey: "createdAt",
        cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return <></>;
          const content = timeSettings.renderTime(value);
          return <span>{content}</span>;
        },
      },
      !isMobile && {
        header: "",
        accessorKey: "hasOperationalHours",
        size: 50,
        cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            <div
              {...tooltipProps}
              data-tooltip-content={`${t("operationalHours")} ${t("enabled")}`}
            >
              <PiClock size={18} />
            </div>
          ) : null;
        },
      },
      !isMobile && {
        id: "floating-menu",
        header: "",
        accessorFn: (row) => row.name,
        meta: {
          disablePreview: true,
          width: "48px",
          minWidth: "48px",
          maxWidth: "48px",
        },
        size: 48,
        minSize: 48,
        maxSize: 48,
        cell: ({ cell }) => {
          return (
            <SiteGroupMenuCell
              siteGroup={cell.row.original}
              permissions={permissions}
              t={t}
              siteGroupsClient={siteGroupsClient}
            />
          );
        },
      },
    ];
    return columns.filter(Boolean) as any;
  }, [
    t,
    timeSettings,
    isMobile,
    tooltipProps,
    permissions,
    siteGroupsClient,
    navigate,
  ]);
};
